import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  HelpCircle,
  Link2,
  MessageSquare,
  PenLine,
  Users,
} from "lucide-react";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetOrganization } from "~/api/organizations/organizations";
import { Loader } from "~/components/Loader";
import { Card, CardContent } from "~/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { customInstance } from "~/custom-instance";
import { getStoredLocale, useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";

type TimePeriod = "month";

const ANALYTICS_DATA_COLLECTION_DAYS = 30;

interface AnalyticsDashboardData {
  efficiency: {
    totalHoursSaved: number;
    avgHoursPerCase: number;
  };
  adoption: {
    totalUsers: number;
    shareableLinks: number;
    featureUsage: {
      chat: number;
      summaries: number;
    };
  };
  reliability: {
    documentsRequiringAttention: {
      count: number;
      processedInPeriod: number;
      byExtension: Array<{ extension: string; count: number }>;
    };
    manualCorrections?: number;
  };
  charts: {
    volume: Array<{ week: string; cases: number }>;
    categories: Array<{ name: string; color: string; sections: number }>;
  };
}

const getAnalyticsDashboard = (
  organizationId: string,
  period: TimePeriod,
  signal?: AbortSignal
) => {
  return customInstance<AnalyticsDashboardData>({
    url: `${import.meta.env.VITE_BASE_API_URL}/organizations/${organizationId}/analytics/dashboard`,
    method: "get",
    params: { period },
    signal,
  });
};

const useGetAnalyticsDashboard = (
  organizationId: string,
  period: TimePeriod
) => {
  return useQuery({
    queryKey: ["analytics", "dashboard", organizationId, period],
    queryFn: ({ signal }) =>
      getAnalyticsDashboard(organizationId, period, signal),
    enabled: !!organizationId,
  });
};

const getDaysSinceCreation = (createdAt: string): number => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffTime = now.getTime() - createdDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const SettingsAnalytics: FC = () => {
  useTrackPageVisit("app/settings_analytics/visit");
  const { organizationId } = useOrganizationParams();
  const storedLocale = getStoredLocale() || "en";
  const intl = useIntl();

  const { data: organization, isLoading: isLoadingOrganization } =
    useGetOrganization(organizationId);
  const { data, isLoading, isError } = useGetAnalyticsDashboard(
    organizationId,
    "month"
  );

  const isOrganizationNew =
    organization?.createdAt &&
    getDaysSinceCreation(organization.createdAt) <
      ANALYTICS_DATA_COLLECTION_DAYS;

  if (isLoading || isLoadingOrganization) {
    return (
      <div className="flex justify-center items-center w-[924px] h-[50vh]">
        <Loader />
      </div>
    );
  }

  if (isOrganizationNew) {
    return (
      <div className="flex flex-col gap-6 mt-2">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage
              id="settings.analytics"
              defaultMessage="Analytics"
            />
          </h3>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.analytics.hint"
              defaultMessage="View ROI, team adoption, and system reliability metrics for your organization"
            />
          </span>
        </div>
        <Card className="w-full max-w-[924px]">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BarChart3 className="w-12 h-12 text-muted-foreground mb-4" />
              <h4 className="text-lg font-medium mb-2">
                <FormattedMessage
                  id="settings.analytics.dataCollecting.title"
                  defaultMessage="We're gathering your analytics data"
                />
              </h4>
              <p className="text-sm text-muted-foreground max-w-md">
                <FormattedMessage
                  id="settings.analytics.dataCollecting.description"
                  defaultMessage="Continue using Sky AI to process your cases and documents. Your analytics dashboard will be available once we have enough data to show meaningful insights."
                />
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Validate that all required data properties exist
  const hasValidData =
    data &&
    data.adoption &&
    typeof data.adoption.totalUsers === "number" &&
    typeof data.adoption.shareableLinks === "number" &&
    data.adoption.featureUsage &&
    typeof data.adoption.featureUsage.chat === "number" &&
    typeof data.adoption.featureUsage.summaries === "number";

  if (isError || !hasValidData) {
    return (
      <div className="flex flex-col gap-6 mt-2">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage
              id="settings.analytics"
              defaultMessage="Analytics"
            />
          </h3>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.analytics.error"
              defaultMessage="Unable to load analytics data. Please try again later."
            />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 mt-2">
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-semibold">
          <FormattedMessage
            id="settings.analytics"
            defaultMessage="Analytics"
          />
        </h3>
        <span className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.analytics.hint.adoption"
            defaultMessage="View team adoption metrics for your organization"
          />
        </span>
      </div>

      <div className="grid grid-cols-2 gap-6 max-w-[612px]">
        <Card className="w-[292px]">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-semibold">
                    <FormattedMessage
                      id="settings.analytics.totalUsers"
                      defaultMessage="Total Users"
                    />
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-[250px]">
                        {intl.formatMessage({
                          id: "settings.analytics.totalUsers.tooltip",
                          defaultMessage:
                            "Total number of team members who have used Sky AI to create cases or upload documents.",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Users className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                {data?.adoption.totalUsers.toLocaleString(storedLocale)}
              </span>
              <span className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="settings.analytics.totalUsers.description"
                  defaultMessage="team members have used Sky"
                />
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="w-[292px]">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-semibold">
                    <FormattedMessage
                      id="settings.analytics.chatUsage"
                      defaultMessage="AI Chat Usage"
                    />
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-[250px]">
                        {intl.formatMessage({
                          id: "settings.analytics.chatUsage.tooltip",
                          defaultMessage:
                            "Percentage of cases where team members have used the AI chat feature to ask questions about documents. Higher usage indicates better adoption of AI-assisted analysis.",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                {data?.adoption.featureUsage.chat}%
              </span>
              <span className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="settings.analytics.casesWithChat"
                  defaultMessage="of cases with chat messages"
                />
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="w-[292px]">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-semibold">
                    <FormattedMessage
                      id="settings.analytics.summariesUsage"
                      defaultMessage="Summaries Usage"
                    />
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-[250px]">
                        {intl.formatMessage({
                          id: "settings.analytics.summariesUsage.tooltip",
                          defaultMessage:
                            "Percentage of processed documents that have AI-generated summaries. Summaries help quickly understand document content without reading the full text.",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <PenLine className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                {data?.adoption.featureUsage.summaries}%
              </span>
              <span className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="settings.analytics.docsWithSummaries"
                  defaultMessage="of documents with summaries"
                />
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="w-[292px]">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-semibold">
                    <FormattedMessage
                      id="settings.analytics.shareableLinks"
                      defaultMessage="Shareable Links Created"
                    />
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-[250px]">
                        {intl.formatMessage({
                          id: "settings.analytics.shareableLinks.tooltip",
                          defaultMessage:
                            "Total number of shareable links created to share case documents with external parties.",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Link2 className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="text-card-foreground text-2xl font-semibold tracking-tight">
                {data?.adoption.shareableLinks.toLocaleString(storedLocale)}
              </span>
              <span className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="settings.analytics.shareableLinks.description"
                  defaultMessage="links created"
                />
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
